import { useLiveSplitQuery } from '@/api/useLiveSplitQuery';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { CombinedQueryResult, QueryConfig } from '@/common/react-query';
import { DEVICE_CELLULAR_SIGNAL_HISTORY_QUERY_KEY } from '@/config';
import { DateRange } from '@/store';

export type DeviceCellularSignalHistoryItem = {
    timestamp: string;
    rsrp: number;
};

export type GetDeviceCellularSignalHistoryResponse = {
    status: string;
    data: DeviceCellularSignalHistoryItem[];
};

export type GetDeviceCellularSignalHistoryParams = {
    spotterId: string;
    startDate: string;
    endDate: string;
};

export const getDeviceCellularSignalHistory = async (
    params: GetDeviceCellularSignalHistoryParams,
    options?: RequestOptions
): Promise<DeviceCellularSignalHistoryItem[]> => {
    const { data } = await axios.get<GetDeviceCellularSignalHistoryResponse, GetDeviceCellularSignalHistoryResponse>(
        '/fetch/device-cellular-signal-history',
        {
            params,
            ...requestConfig(options),
        }
    );
    return data;
};

type UseDeviceCellularSignalHistoryOptions = {
    spotterId?: string | null;
    dateRange?: DateRange | null;
    config?: Omit<QueryConfig<typeof getDeviceCellularSignalHistory>, 'select'>;
};

export const useDeviceCellularSignalHistory = ({
    spotterId,
    dateRange,
    config,
}: UseDeviceCellularSignalHistoryOptions): CombinedQueryResult<DeviceCellularSignalHistoryItem[]> =>
    useLiveSplitQuery({
        queryKeyPrefix: DEVICE_CELLULAR_SIGNAL_HISTORY_QUERY_KEY,
        spotterId,
        dateRange,
        fetchRange: ({ viewToken, signal, ...params }) => getDeviceCellularSignalHistory(params, { viewToken, signal }),
        // one of the newer device status entrypoints, so it excludes the end of the window it is given
        apiDateBounds: 'closedOpen',
        timestampKey: 'timestamp',
        sortOrder: 'asc',
        /*
         * wavefleet sorts this window oldest first across every device row it queried. Ordered here so the
         * trimming and clipping that reassemble the windows own the order they need.
         */
        responseSortOrder: 'unsorted',
        config,
    });
